import React, { useMemo } from "react";
import { PANEL_RESIZE_PROFILES } from "../../../config/itineraryPanel.configuration";
import ReturnBackIcon from "../../../image/icons/navigation/ReturnBackIcon.svg?react";
import { ephemeralStore, navigationSessionSlice } from "../../../store/ephemeral";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type Route } from "../../../types/Forms";
import { NavigationMode } from "../../../types/Navigation";
import { type ItineraryDetailsOfferLinkDisplay } from "../itineraryDetails/itineraryDetailsOfferLink.types";
import ItineraryDetailsOfferPricePill from "../itineraryDetails/ItineraryDetailsOfferPricePill";
import ItineraryDetailsSaveToggle from "../itineraryDetails/ItineraryDetailsSaveToggle";
import ItineraryDetailsTimingPill from "../itineraryDetails/ItineraryDetailsTimingPill";
import {
    itineraryPanelResizeAdapter,
    PanelResizeHandle,
    type PanelResizeLayout,
} from "../panelResize";
import {
    buildItineraryPricePillChromeOutlineStyle,
    ITINERARY_PRICE_PILL_CHROME_OUTLINE_MY_CAR_CLASSNAME,
} from "../pricing/itineraryPricePill.styles";
import PriceConversion from "../pricing/PriceConversion";

/**
 * Chrome layout mode for the itinerary panel top row.
 */
type ItineraryPanelChromeMode = "itineraryDetails" | "carSelection";

/**
 * Props for the unified itinerary panel chrome row (back, timing, drag handle, price).
 */
interface ItineraryPanelChromeRowProps {
    "mode": ItineraryPanelChromeMode;

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Whether Euro currency formatting is active. */
    "isEuro": boolean;

    /** Shared resize layout measurements from Home. */
    "panelResizeLayout": PanelResizeLayout;

    /** Resets the bottom panel to the expanded snap tier (car selection abandon, etc.). */
    "onResetPanelExpanded": () => void;

    /** Restores the last remembered itinerary-list snap tier when leaving itinerary details. */
    "onRestoreItineraryListPanelSnap"?: () => void;

    /** Itinerary details mode: selected itinerary and offer chrome data. */
    "selectedItinerary"?:                ItineraryDTO | null;
    "offerStatus"?:                      "idle" | "loading" | "success" | "error";
    "computedOfferTotalPrice"?:          number | null;
    "showIncompleteOfferTotalFootnote"?: boolean;
    "pricingPreferences"?:               ItineraryPricingPreferencesDTO;
    "rentalItineraryOfferLink"?:         ItineraryDetailsOfferLinkDisplay | null;

    /** Whether the selected itinerary is already saved (itineraryDetails mode only). */
    "isSaved"?: boolean;

    /** Persists or removes the saved itinerary; omit to hide the save toggle. */
    "onSaveToggle"?: () => void;

    /** When `false`, hides the chrome price badge (saved-itinerary details). */
    "showPrice"?: boolean;

    /** Shared back-navigation handlers (itinerary details mode). */
    "setShowArrowTop"?:                DispatchLike<boolean>;
    "setSelectedItinerary"?:           DispatchLike<ItineraryDTO | null>;
    "setItineraryPanelViewDirection"?: ( direction: "forward" | "back" ) => void;
    "handleResetMap"?:                 () => void;

    /** Car selection mode: personal-car itinerary used for the timing capsule. */
    "carItinerary"?: ItineraryDTO | null;

    /** Car selection mode: resolved personal-car total price in CHF, when known. */
    "carTotalPriceChf"?: number | null;

    /** Opens the personal-car price breakdown modal (car selection mode). */
    "onOpenCarPriceDetail"?: () => void;

    /** Car selection mode handlers. */
    "setITakeMyCar"?:              DispatchLike<boolean>;
    "setSelectedCarRouteId"?:      DispatchLike<string>;
    "setItineraries"?:             DispatchLike<Route[]>;
    "setGoItineraries"?:           DispatchLike<Route[]>;
    "setBackupReturnItineraries"?: DispatchLike<Route[]>;
}

type DispatchLike<T> = ( value: T ) => void;

const ITINERARY_DETAILS_CHROME_HEIGHT_PX = PANEL_RESIZE_PROFILES.itineraryDetails.chromeRowHeightPx;

/**
 * Sets navigation mode in the ephemeral navigation session slice.
 *
 * @param mode - Target navigation mode after leaving itinerary details.
 */
const setNavigationMode = ( mode: NavigationMode ): void => {
    ephemeralStore.setValue( navigationSessionSlice, ( previous ) => ( {
        ...previous,
        "navigationMode": mode,
    } ) );
};

/**
 * Renders the itinerary panel top chrome: glass back control, unified timing/save capsule,
 * centered overlay drag handle, and a filled price stamp on the right.
 *
 * @remarks Car selection reuses the same chrome shell as itinerary details (borders, glass
 * controls, timing capsule) but omits the resize handle so panel height stays fixed in that view.
 */
const ItineraryPanelChromeRow: React.FC<ItineraryPanelChromeRowProps> = ( {
    mode,
    isDarkUi,
    isEuro,
    panelResizeLayout,
    onResetPanelExpanded,
    onRestoreItineraryListPanelSnap,
    selectedItinerary = null,
    offerStatus = "idle",
    computedOfferTotalPrice = null,
    showIncompleteOfferTotalFootnote = false,
    pricingPreferences,
    rentalItineraryOfferLink = null,
    isSaved = false,
    onSaveToggle,
    showPrice = true,
    setShowArrowTop,
    setSelectedItinerary,
    setItineraryPanelViewDirection,
    handleResetMap,
    carItinerary = null,
    carTotalPriceChf = null,
    onOpenCarPriceDetail,
    setITakeMyCar,
    setSelectedCarRouteId,
    setItineraries,
    setGoItineraries,
    setBackupReturnItineraries,
} ) => {
    const handleItineraryDetailsBack = (): void => {
        setItineraryPanelViewDirection?.( "back" );
        onRestoreItineraryListPanelSnap?.();
        setShowArrowTop?.( false );
        setSelectedItinerary?.( null );
        handleResetMap?.();
        setNavigationMode( NavigationMode.EXPLORATION );
    };

    const handleCarSelectionBack = (): void => {
        setItineraryPanelViewDirection?.( "back" );
        setITakeMyCar?.( false );
        setSelectedCarRouteId?.( "" );
        onResetPanelExpanded();
        setItineraries?.( [] );
        setGoItineraries?.( [] );
        setBackupReturnItineraries?.( [] );
    };

    const isCarSelection = mode === "carSelection";
    const showItineraryDetailsChrome = mode === "itineraryDetails" && selectedItinerary != null && pricingPreferences != null;
    const showCarTimingChrome = isCarSelection && carItinerary != null;
    const hasCarPrice = typeof carTotalPriceChf === "number" && Number.isFinite( carTotalPriceChf );
    const palette = getUIColorPalette( isDarkUi );
    const timingAccent = palette.itineraryDetailsTimingPillAccent;
    const myCarPriceAccent = palette.myCarPricePillAccent;

    const timingCapsuleStyle = useMemo( (): React.CSSProperties => ( {
        "borderColor": palette.overlayBorderStrong,
        "background":  `linear-gradient(118deg, ${ timingAccent }22 0%, ${ palette.overlayCardBackgroundStrong } 58%, ${ palette.overlayCardBackgroundSoft } 100%)`,
    } ), [ palette.overlayBorderStrong, palette.overlayCardBackgroundSoft, palette.overlayCardBackgroundStrong, timingAccent ] );
    const backIconColor = useMemo( (): string => {
        const iconBackground = isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft;

        return `color-mix(in srgb, ${ palette.overlayTextSecondary } 68%, ${ iconBackground })`;
    }, [ isDarkUi, palette.overlayCardBackgroundSoft, palette.overlayCardBackgroundStrong, palette.overlayTextSecondary ] );

    const backButtonStyle = useMemo( (): React.CSSProperties => ( {
        "borderColor": palette.overlayBorderStrong,
        "background":  isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft,
        "color":       backIconColor,
    } ), [ backIconColor, isDarkUi, palette.overlayBorderStrong, palette.overlayCardBackgroundSoft, palette.overlayCardBackgroundStrong ] );

    const carPricePillStyle = useMemo(
        (): React.CSSProperties => buildItineraryPricePillChromeOutlineStyle( myCarPriceAccent ),
        [ myCarPriceAccent ],
    );

    return (
        <div
            className="relative z-10 flex w-full shrink-0 items-center gap-1 border-b border-solid p-2 pt-4 pb-2 backdrop-blur-3xl md:rounded-t-2xl"
            id={isCarSelection ? "itinerary-car-selection-chrome-row" : "itinerary-details-chrome-row"}
            style={{
                "borderBottomColor": palette.overlayBorder,
                "height":            `${ ITINERARY_DETAILS_CHROME_HEIGHT_PX }px`,
            }}
        >
            <button
                aria-label="Retour"
                className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-solid backdrop-blur-sm transition-transform duration-150 ease-out active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                id={isCarSelection ? "itinerary-car-selection-back-button" : "itinerary-details-back-button"}
                onClick={isCarSelection ? handleCarSelectionBack : handleItineraryDetailsBack}
                style={backButtonStyle}
                type="button"
            >
                <ReturnBackIcon
                    aria-hidden={true}
                    className="h-[18px] w-[18px]"
                />
            </button>
            {showItineraryDetailsChrome
                ? (
                    <div
                        className="relative z-10 inline-flex h-9 min-w-0 max-w-full items-center overflow-hidden rounded-2xl border border-solid backdrop-blur-sm"
                        id="itinerary-details-timing-capsule"
                        style={timingCapsuleStyle}
                    >
                        <div
                            className="min-w-0 shrink"
                            id="itinerary-details-timing-pill"
                        >
                            <ItineraryDetailsTimingPill
                                isDarkUi={isDarkUi}
                                itinerary={selectedItinerary}
                                variant="embedded"
                            />
                        </div>
                        {onSaveToggle !== undefined
                            ? (
                                <>
                                    <div
                                        aria-hidden={true}
                                        className="h-4 w-px shrink-0 self-center"
                                        id="itinerary-details-save-divider"
                                        style={{ "backgroundColor": palette.overlayBorderStrong }}
                                    />
                                    <ItineraryDetailsSaveToggle
                                        isDarkUi={isDarkUi}
                                        isSaved={isSaved}
                                        onToggle={onSaveToggle}
                                        variant="embedded"
                                    />
                                </>
                            )
                            : null}
                    </div>
                )
                : null}
            {showCarTimingChrome && carItinerary != null
                ? (
                    <div
                        className="relative z-10 inline-flex h-9 min-w-0 max-w-full items-center overflow-hidden rounded-2xl border border-solid backdrop-blur-sm"
                        id="itinerary-car-selection-timing-capsule"
                        style={timingCapsuleStyle}
                    >
                        <div
                            className="min-w-0 shrink"
                            id="itinerary-car-selection-timing-pill"
                        >
                            <ItineraryDetailsTimingPill
                                isDarkUi={isDarkUi}
                                itinerary={carItinerary}
                                variant="embedded"
                            />
                        </div>
                    </div>
                )
                : null}
            {showItineraryDetailsChrome && showPrice
                ? (
                    <div
                        className="relative z-10 ml-auto shrink-0"
                        id="itinerary-details-price-pill"
                    >
                        <ItineraryDetailsOfferPricePill
                            computedOfferTotalPrice={computedOfferTotalPrice}
                            isDarkUi={isDarkUi}
                            isEuro={isEuro}
                            itinerary={selectedItinerary}
                            offerStatus={offerStatus}
                            pricingPreferences={pricingPreferences}
                            rentalItineraryOfferLink={rentalItineraryOfferLink}
                            showIncompleteOfferTotalFootnote={showIncompleteOfferTotalFootnote}
                            variant="chrome"
                        />
                    </div>
                )
                : null}
            {isCarSelection && hasCarPrice && carTotalPriceChf != null
                ? (
                    <button
                        aria-label="Détail du prix voiture"
                        className={`relative z-10 ml-auto shrink-0 cursor-pointer ${ ITINERARY_PRICE_PILL_CHROME_OUTLINE_MY_CAR_CLASSNAME }`}
                        id="itinerary-car-selection-price-pill"
                        onClick={() => {
                            onOpenCarPriceDetail?.();
                        }}
                        style={carPricePillStyle}
                        type="button"
                    >
                        <PriceConversion
                            chooseEuro={isEuro}
                            className="text-inherit"
                            compact={true}
                            price={carTotalPriceChf}
                        />
                    </button>
                )
                : null}
            {isCarSelection
                ? null
                : (
                    <PanelResizeHandle
                        adapter={itineraryPanelResizeAdapter}
                        containerRef={panelResizeLayout.containerRef}
                        isDarkUi={isDarkUi}
                        layout="overlay"
                        layoutBounds={panelResizeLayout}
                        profile={PANEL_RESIZE_PROFILES.itineraryDetails}
                    />
                )}
        </div>
    );
};

export default ItineraryPanelChromeRow;
